import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/lib/useSeo";
import {
    EnvelopeSimple, Buildings, User, ArrowRight,
    Compass, ShieldCheck, Question, PaperPlaneTilt, CheckCircle,
} from "@phosphor-icons/react";

const CONTACT_EMAIL = "info@naukridaddy.com";
const GRIEVANCE_EMAIL = "grievance@naukridaddy.com";

const BUCKETS = [
    {
        icon: User,
        eyebrow: "For Candidates",
        title: "Job seekers, referrals, & premium",
        blurb:
            "Payment help, verified badge, Interview Prep, referral bounties, " +
            "account deletion (DPDPA) — write to us and we'll respond within 48 hours (working days).",
        subject: "Candidate support — NaukriDaddy",
        testid: "contact-bucket-candidate",
        accent: "text-cobalt",
        border: "border-cobalt",
    },
    {
        icon: Buildings,
        eyebrow: "For Agencies & Clients",
        title: "Recruiter, workspace & sales enquiries",
        blurb:
            "SaaS pricing, Talent Marketplace access, plan upgrades, custom domain " +
            "(CNAME), API integration, invoice / GST queries — reach out directly.",
        subject: "Agency / Client enquiry — NaukriDaddy",
        testid: "contact-bucket-agency",
        accent: "text-splash",
        border: "border-splash",
    },
];

export default function ContactUs() {
    useSeo({
        title: "Contact NaukriDaddy · info@naukridaddy.com | Delhi, India",
        description: "Talk to a human. Support, sales, agency partnerships, press — one inbox, real replies, 48-hour SLA. Naukridaddy HR Solutions Private Limited · Delhi, India.",
        canonical: "https://naukridaddy.com/contact",
    });
    const [form, setForm] = useState({
        name: "", email: "", role: "candidate", message: "", website: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (form.message.trim().length < 10) {
            return toast.error("Please write at least a few sentences.");
        }
        setSubmitting(true);
        try {
            await api.post("/contact/submit", form);
            setSent(true);
            toast.success("Message sent — we'll reply within 48 hours.");
            setForm({ name: "", email: "", role: form.role, message: "", website: "" });
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Couldn't send — try again or email us directly.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-white" data-testid="contact-page">
            {/* Header */}
            <header className="border-b border-[#E5E5E5]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="font-heading text-2xl font-black tracking-tighter text-ink" data-testid="contact-home-link">
                        NaukriDaddy
                    </Link>
                    <div className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] inline-flex items-center gap-1">
                        <Compass size={12} weight="fill" /> Delhi · India
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-8">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-3">Get in touch</p>
                <h1 className="font-heading text-4xl sm:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
                    Talk to a <span className="text-cobalt">human.</span>
                </h1>
                <p className="text-base sm:text-lg text-[#404040] max-w-2xl leading-relaxed">
                    We read every message ourselves. Candidates, agencies, journalists,
                    investors — one inbox, real replies, no bots.
                </p>

                {/* Primary email — huge, tappable */}
                <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    data-testid="contact-primary-email"
                    className="mt-8 inline-flex items-center gap-3 bg-ink text-white px-5 sm:px-6 py-4 group hover:bg-cobalt transition-colors"
                >
                    <EnvelopeSimple size={20} weight="fill" className="text-splash" />
                    <span className="font-heading text-xl sm:text-2xl font-black tracking-tight">
                        {CONTACT_EMAIL}
                    </span>
                    <ArrowRight size={16} weight="bold" className="opacity-60 group-hover:translate-x-1 transition-transform" />
                </a>
                <p className="text-[10px] text-[#A3A3A3] mt-3">
                    Response SLA · <b className="text-ink">48 hours</b> on working days (Mon–Sat).
                </p>
            </section>

            {/* Buckets */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                <div className="grid gap-4 md:grid-cols-2">
                    {BUCKETS.map((b, i) => (
                        <div
                            key={b.testid}
                            data-testid={b.testid}
                            className={`bg-white border-2 ${b.border} p-6 sm:p-7`}
                        >
                            <div className="flex items-center gap-2 mb-4">
                                <div className={`w-10 h-10 flex items-center justify-center bg-[#FAFAFA] ${b.accent}`}>
                                    <b.icon size={18} weight="fill" />
                                </div>
                                <p className={`text-[10px] font-black uppercase tracking-[0.2em] ${b.accent}`}>
                                    {b.eyebrow}
                                </p>
                            </div>
                            <h2 className="font-heading text-2xl font-black tracking-tighter text-ink mb-2">
                                {b.title}
                            </h2>
                            <p className="text-sm text-[#525252] leading-relaxed mb-5">{b.blurb}</p>
                            <a
                                href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(b.subject)}`}
                                data-testid={`${b.testid}-mailto`}
                                className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-ink hover:text-cobalt"
                            >
                                <EnvelopeSimple size={13} weight="bold" />
                                {CONTACT_EMAIL} <ArrowRight size={12} weight="bold" />
                            </a>
                        </div>
                    ))}
                </div>
            </section>

            {/* Inline contact form */}
            <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="contact-form-section">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-3">
                    Or write to us right here
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-ink mb-2">
                    Send a message.
                </h2>
                <p className="text-sm text-[#525252] mb-6">
                    Goes straight to our team Slack + inbox. We reply within 48 working hours.
                </p>

                {sent ? (
                    <div className="bg-emerald-50 border-2 border-emerald-500 p-6 flex items-start gap-3" data-testid="contact-form-success">
                        <CheckCircle size={24} weight="fill" className="text-emerald-600 shrink-0" />
                        <div>
                            <p className="font-heading text-lg font-black text-ink">Got it — message on its way.</p>
                            <p className="text-sm text-[#404040] mt-1">
                                We&apos;ll reply within 48 hours. In the meantime,
                                {" "}<Link to="/" className="text-cobalt underline">head back to the home page</Link> or
                                {" "}<button onClick={() => setSent(false)} className="text-cobalt underline">send another one</button>.
                            </p>
                        </div>
                    </div>
                ) : (
                <form onSubmit={submit} className="bg-white border border-[#E5E5E5] p-6 sm:p-7 space-y-4" data-testid="contact-form">
                    {/* Honeypot — hidden from humans, filled by bots */}
                    <label className="absolute -left-[9999px]" aria-hidden="true">
                        Website
                        <input type="text" tabIndex={-1} autoComplete="off"
                               value={form.website} onChange={update("website")} />
                    </label>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1.5">Your name</label>
                            <input
                                required minLength={2} maxLength={80}
                                data-testid="contact-form-name"
                                value={form.name} onChange={update("name")}
                                placeholder="Priya Sharma"
                                className="w-full px-3 py-2.5 text-sm border border-[#E5E5E5] focus:outline-none focus:border-ink"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1.5">Email</label>
                            <input
                                required type="email" maxLength={120}
                                data-testid="contact-form-email"
                                value={form.email} onChange={update("email")}
                                placeholder="you@example.com"
                                className="w-full px-3 py-2.5 text-sm border border-[#E5E5E5] focus:outline-none focus:border-ink"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1.5">I&apos;m a</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup" data-testid="contact-form-role">
                            {[
                                { v: "candidate", l: "Candidate" },
                                { v: "agency",    l: "Agency / Client" },
                                { v: "press",     l: "Press" },
                                { v: "other",     l: "Other" },
                            ].map(o => (
                                <label key={o.v}
                                       data-testid={`contact-form-role-${o.v}`}
                                       className={`cursor-pointer text-center text-xs font-black uppercase tracking-wider py-2.5 border transition-colors ${form.role === o.v ? "bg-ink text-white border-ink" : "bg-white border-[#E5E5E5] hover:border-ink"}`}>
                                    <input type="radio" name="role" value={o.v}
                                           checked={form.role === o.v}
                                           onChange={update("role")}
                                           className="sr-only" />
                                    {o.l}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1.5">Message</label>
                        <textarea
                            required minLength={10} maxLength={2000} rows={5}
                            data-testid="contact-form-message"
                            value={form.message} onChange={update("message")}
                            placeholder="What can we help with? The more context, the faster we can reply."
                            className="w-full px-3 py-2.5 text-sm border border-[#E5E5E5] focus:outline-none focus:border-ink resize-y"
                        />
                        <div className="flex justify-between mt-1 text-[10px] text-[#A3A3A3]">
                            <span>Min 10 characters · no more than 3 links</span>
                            <span>{form.message.length}/2000</span>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                        <p className="text-[10px] text-[#A3A3A3]">
                            By sending, you agree to our <Link to="/policies/privacy" className="text-cobalt underline">privacy policy</Link>.
                        </p>
                        <button type="submit" disabled={submitting}
                                data-testid="contact-form-submit"
                                className="inline-flex items-center gap-2 bg-ink text-white px-5 py-2.5 text-xs font-black uppercase tracking-wider hover:bg-cobalt transition-colors disabled:opacity-50">
                            <PaperPlaneTilt size={13} weight="bold" />
                            {submitting ? "Sending…" : "Send message"}
                        </button>
                    </div>
                </form>
                )}
            </section>

            {/* Other channels */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-4">
                    Other ways to reach us
                </p>
                <div className="grid gap-3 md:grid-cols-3">
                    <div className="bg-[#FAFAFA] p-5" data-testid="contact-channel-grievance">
                        <div className="flex items-center gap-2 mb-2">
                            <ShieldCheck size={14} weight="bold" className="text-emerald-600" />
                            <p className="text-[10px] font-black uppercase tracking-wider text-ink">Grievance Officer</p>
                        </div>
                        <a href={`mailto:${GRIEVANCE_EMAIL}`} className="text-sm text-cobalt underline break-all">
                            {GRIEVANCE_EMAIL}
                        </a>
                        <p className="text-[10px] text-[#A3A3A3] mt-2 leading-relaxed">
                            Under Rule 5(9) of IT Rules, 2021 · DPDPA-compliant escalations.
                        </p>
                    </div>
                    <div className="bg-[#FAFAFA] p-5" data-testid="contact-channel-registered">
                        <div className="flex items-center gap-2 mb-2">
                            <Buildings size={14} weight="bold" className="text-ink" />
                            <p className="text-[10px] font-black uppercase tracking-wider text-ink">Registered Office</p>
                        </div>
                        <p className="text-sm text-[#404040] leading-tight">
                            Naukridaddy HR Solutions Private Limited<br />
                            Delhi, India
                        </p>
                        <p className="text-[10px] text-[#A3A3A3] mt-2">
                            Postal correspondence · use the primary email above for fastest response.
                        </p>
                    </div>
                    <div className="bg-[#FAFAFA] p-5" data-testid="contact-channel-policies">
                        <div className="flex items-center gap-2 mb-2">
                            <Question size={14} weight="bold" className="text-splash" />
                            <p className="text-[10px] font-black uppercase tracking-wider text-ink">Before you write</p>
                        </div>
                        <div className="space-y-1 text-sm">
                            <Link to="/policies/refund" className="block text-cobalt hover:text-ink underline">Refund policy</Link>
                            <Link to="/policies/privacy" className="block text-cobalt hover:text-ink underline">Privacy &amp; DPDPA</Link>
                            <Link to="/policies/grievances" className="block text-cobalt hover:text-ink underline">Grievance redressal</Link>
                            <Link to="/policies/fraud" className="block text-cobalt hover:text-ink underline">Fraud prevention</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer strip */}
            <section className="border-t border-[#E5E5E5]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap justify-between items-center gap-3 text-[11px] text-[#A3A3A3]">
                    <span>© {new Date().getFullYear()} Naukridaddy HR Solutions Private Limited</span>
                    
                </div>
            </section>
        </div>
    );
}
